// MeetFlyConfirm — the confirm card of a SPOKEN Fly ask in a call
// (functions/meetFly.js meetFlyConfirm; workspace docs/FLY_MODE.md §3.3).
// Nika 2026-10-26: "during call when we mention fly, symbols or simone,
// before rendering fly content it should open confirm prompt of what was
// required to render"; her answer the same night: a card with what was heard,
// Show and Cancel, and a spoken "yes" or "no". The card names the agent and
// what the call heard; Show makes the page, Cancel drops it, and the asker
// may say "yes" or "no" instead. Only the asker sees it — the confirm is this
// client's own state — and it leaves by itself after 30 s.
//
// Mounted ONCE at the shell root (workspace app.js, beside MeetCallWidget):
// the asker can be on the call view, on its theater or on any other route.
// It reads the call through `meetFlyConfirmOf` (functions/meetFlyState.js,
// registered on every route by meet's eager function slice); its two buttons
// reach meetFly.js through the shell context, where the meet context is
// folded by then (a confirm exists only once meetFly.js heard the ask). A
// name that is not registered is skipped quietly.
//
// CHROME: the shell's transient-card family (workspace CallInvitationBanner,
// IncomingCallBanner): `background: 'document'`, `round: 'radiusSheet'`,
// `shadow: 'overlay'`, bottom-centre above the composer — one home for the
// transient shell messages (Nika 2026-09-01). The wrapper is
// `display: 'contents'` so it never becomes a grid child of the shell.
export const MeetFlyConfirm = {
  tag: 'div',
  display: 'contents',
  subscribeTo: ['meetFly', 'livekitConnected', 'activeRoomId'],

  Card: {
    hide: (el, s) => !el.call('meetFlyConfirmOf', s.root),
    position: 'fixed',
    bottom: 'C1',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: '1600',
    background: 'document',
    round: 'radiusSheet',
    shadow: 'overlay',
    flow: 'y',
    gap: 'Z1',
    padding: 'B',
    width: 'H1',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    boxSizing: 'border-box',
    animationName: 'fadeIn',
    animationDuration: 'C',
    animationTimingFunction: 'defaultBezier',
    animationFillMode: 'both',
    role: 'status',
    ariaLive: 'polite',
    'aria-label': (el) => el.call('polyglot', 'meet.fly.confirmLabel'),
    '@mobileL': {
      left: 'Z',
      right: 'Z',
      transform: 'none',
      width: 'auto',
      maxWidth: 'none'
    },

    Header: {
      flow: 'x',
      align: 'center',
      gap: 'Y1',
      minWidth: '0',

      Icon: { name: 'pxFly', boxSize: 'A', color: 'caption' },
      Eyebrow: {
        tag: 'span',
        minWidth: '0',
        fontSize: 'Y',
        fontWeight: '500',
        textTransform: 'uppercase',
        letterSpacing: '.07em',
        color: 'caption',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => {
          const c = el.call('meetFlyConfirmOf', s.root)
          return (c && c.agent && c.agent.name) || el.call('polyglot', 'meet.fly.aiName')
        }
      }
    },

    Heard: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: '{{ meet.fly.confirmHeard | polyglot }}'
    },

    Prompt: {
      tag: 'p',
      margin: '0',
      minWidth: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      lineHeight: '1.3',
      lineClamp: '3',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
      text: (el, s) => {
        const c = el.call('meetFlyConfirmOf', s.root)
        if (!c) return ''
        // A function, so a "$&" the transcript wrote stays as written.
        return String(el.call('polyglot', 'meet.fly.confirmQuote') || '{prompt}').replace(
          '{prompt}',
          () => c.prompt
        )
      }
    },

    Footer: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',
      marginTop: 'V',

      Hint: {
        tag: 'span',
        minWidth: '0',
        fontSize: 'Z2',
        color: 'caption',
        text: '{{ meet.fly.confirmHint | polyglot }}'
      },

      Actions: {
        flow: 'x',
        align: 'center',
        gap: 'Z',

        // Cancel: nothing is made.
        AppCapsuleBtnGhost: {
          text: '{{ meet.fly.confirmCancel | polyglot }}',
          onClick: (e, el) => {
            e.stopPropagation()
            const fns = el.context && el.context.functions
            if (fns && typeof fns.meetFlyConfirmAnswer === 'function') {
              el.call('meetFlyConfirmAnswer', false)
            }
          }
        },

        // Show: the page is made, as the asker.
        AppCapsuleBtnPrimary: {
          text: '{{ meet.fly.confirmShow | polyglot }}',
          onClick: (e, el) => {
            e.stopPropagation()
            const fns = el.context && el.context.functions
            if (fns && typeof fns.meetFlyConfirmAnswer === 'function') {
              el.call('meetFlyConfirmAnswer', true)
            }
          }
        }
      }
    }
  }
}
